import { bootstrapApplication } from "@angular/platform-browser";
import { AppComponent } from "./app/app.component";
import { appConfig } from "./app/app.config";
import { environment } from "./environments/environment";
import {
  mergeApplicationConfig,
  ApplicationConfig,
  isDevMode,
} from "@angular/core";
import { provideServiceWorker } from "@angular/service-worker";

/**
 * Global Console Override Mechanism
 * This ensures that console.log and console.debug are only active under special circumstances.
 */
const initializeConsoleOverrides = () => {
  const originalLog = console.log;
  const originalDebug = console.debug;
  const expectedToken = "enabled";

  // Check multiple sources for the debug token
  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get("debug_token");
  const localStorageToken = localStorage.getItem("APP_DEBUG_TOKEN");

  const isDebugEnabled =
    environment.aidboxDebug.enabled ||
    urlToken === expectedToken ||
    localStorageToken === expectedToken;

  if (isDebugEnabled) {
    originalLog(`[LOGGER] Debug mode enabled. All console logs will be shown.`);
    // If enabled via URL, save to localStorage for persistence across navigation
    if (urlToken === expectedToken) {
      localStorage.setItem("APP_DEBUG_TOKEN", expectedToken);
    }
    return;
  }

  // Override log and debug to be no-ops
  // We keep warn and error for critical issues
  console.log = () => {};
  console.debug = () => {};

  originalLog(
    `[LOGGER] Console debug logs have been suppressed. Use ?debug_token=${expectedToken} or set localStorage.APP_DEBUG_TOKEN to enable.`,
  );
};

// Initialize overrides before bootstrapping
initializeConsoleOverrides();

// Service Worker configuration for version update detection.
// Only enable in production/staging builds — never in development or local dev server,
// where ngsw-worker.js does not exist and a stale SW would intercept all network requests.
const swEnabled =
  environment.production || (!isDevMode() && !("local" in environment));

const swConfig: ApplicationConfig = {
  providers: [
    provideServiceWorker("ngsw-worker.js", {
      enabled: swEnabled,
      registrationStrategy: "registerWhenStable:30000", // Wait 30s after app stabilizes
    }),
  ],
};

// Merge SW config so SwUpdate stays in the DI tree; registration is gated by `swEnabled`.
const finalConfig = mergeApplicationConfig(appConfig, swConfig);

bootstrapApplication(AppComponent, finalConfig).catch((err) =>
  console.error(err),
);
